/**
 * The text of an AI Assistant answer, as blocks to draw (pilot feedback, 2026-10-23). The model
 * writes a little Markdown: paragraphs, lists with "- " or "1. ", and **bold**. The screens showed
 * the stars and dashes as they are. This parser knows only that small set, so Desk, Control Center
 * and the mobile app draw the same answer. Tables came later (pilot, 2026-10-23): the model wrote a
 * "| Level | Time | Marked by |" table, and the screens showed the bars.
 *
 * It returns data, never HTML: each screen draws the parts as its own text elements, so a name in
 * the data cannot add markup. Anything that this parser does not know (a link, code, a quote) stays
 * as plain text.
 */

/** One run of text in a line. `bold` from `**text**` or `__text__`, `italic` from `*text*` or `_text_`. */
export type AssistantTextSpan = { text: string; bold?: true; italic?: true };

export type AssistantTextBlock =
  | { type: "paragraph"; spans: AssistantTextSpan[] }
  | { type: "list"; ordered: boolean; start: number; items: AssistantTextSpan[][] }
  /** `header` is plain text. Each row has one cell for each header cell. `end` aligns a column right (`--:`). */
  | { type: "table"; header: string[]; align: ("start" | "end")[]; rows: AssistantTextSpan[][][] };

const BULLET = /^\s*[-*•]\s+(.*)$/;
const NUMBERED = /^\s*(\d{1,3})[.)]\s+(.*)$/;
/** "## Title" and "**Title**" alone on a line are both a short heading: a bold paragraph. */
const HEADING = /^\s{0,3}#{1,6}\s+(.*?)\s*#*\s*$/;

/** The line under the header of a table: "|---|:--:|---:|". */
const TABLE_RULE = /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/;

/** The cells of one table line. `\|` is a bar in a cell, not a cell edge. */
function tableCells(line: string): string[] {
  const cells: string[] = [];
  let cell = "";
  const text = line.trim().replace(/^\|/, "");
  for (let index = 0; index < text.length; index += 1) {
    const char = text[index]!;
    if (char === "\\" && text[index + 1] === "|") {
      cell += "|";
      index += 1;
    } else if (char === "|") {
      cells.push(cell.trim());
      cell = "";
    } else {
      cell += char;
    }
  }
  if (cell.trim() !== "") cells.push(cell.trim());
  return cells;
}

/** The answer as paragraphs, lists and tables. Blank lines split paragraphs; lines in one paragraph keep their breaks. */
export function parseAssistantText(text: string): AssistantTextBlock[] {
  const blocks: AssistantTextBlock[] = [];
  let paragraph: string[] = [];
  let list: { ordered: boolean; start: number; items: string[] } | null = null;

  const flushParagraph = () => {
    if (paragraph.length > 0) blocks.push({ type: "paragraph", spans: parseAssistantInline(paragraph.join("\n")) });
    paragraph = [];
  };
  const flushList = () => {
    if (list) blocks.push({ type: "list", ordered: list.ordered, start: list.start, items: list.items.map(parseAssistantInline) });
    list = null;
  };

  const lines = text.replace(/\r\n?/g, "\n").split("\n");
  for (let at = 0; at < lines.length; at += 1) {
    const line = lines[at]!;
    // A table: a line with bars, then the rule line with the same number of cells. A table of one
    // column needs a bar in its rule too, so "Note |" over a "---" line stays text.
    const header = line.includes("|") ? tableCells(line) : [];
    const rule = lines[at + 1];
    if (
      header.length > 0 &&
      rule !== undefined &&
      TABLE_RULE.test(rule) &&
      tableCells(rule).length === header.length &&
      (header.length > 1 || rule.includes("|"))
    ) {
      flushParagraph();
      flushList();
      const rows: AssistantTextSpan[][][] = [];
      at += 2;
      while (at < lines.length && lines[at]!.includes("|") && lines[at]!.trim() !== "") {
        const cells = tableCells(lines[at]!);
        rows.push(header.map((_, column) => parseAssistantInline(cells[column] ?? "")));
        at += 1;
      }
      at -= 1;
      blocks.push({
        type: "table",
        header: header.map((cell) => parseAssistantInline(cell).map((span) => span.text).join("")),
        align: tableCells(rule).map((cell) => (/-:$/.test(cell) ? "end" : "start")),
        rows,
      });
      continue;
    }
    const bullet = BULLET.exec(line);
    const numbered = bullet ? null : NUMBERED.exec(line);
    if (bullet || numbered) {
      flushParagraph();
      const ordered = numbered !== null;
      if (list && list.ordered !== ordered) flushList();
      if (!list) list = { ordered, start: numbered ? Number(numbered[1]) : 1, items: [] };
      list.items.push(bullet ? bullet[1]! : numbered![2]!);
      continue;
    }
    if (line.trim() === "") {
      flushParagraph();
      flushList();
      continue;
    }
    // A line that is indented under a list item belongs to that item.
    if (list && /^\s{2,}\S/.test(line)) {
      list.items[list.items.length - 1] += ` ${line.trim()}`;
      continue;
    }
    flushList();
    const heading = HEADING.exec(line);
    if (heading) {
      flushParagraph();
      blocks.push({ type: "paragraph", spans: [{ text: heading[1]!, bold: true }] });
      continue;
    }
    paragraph.push(line);
  }
  flushParagraph();
  flushList();
  return blocks;
}

/**
 * The bold and italic runs of one line. A marker with no partner stays as text, so "5 * 3" or a
 * name with an underscore shows as it is.
 */
export function parseAssistantInline(text: string): AssistantTextSpan[] {
  const spans: AssistantTextSpan[] = [];
  const push = (span: AssistantTextSpan) => {
    if (span.text === "") return;
    const last = spans.at(-1);
    if (last && last.bold === span.bold && last.italic === span.italic) last.text += span.text;
    else spans.push(span);
  };
  // `**x**` and `__x__` first, then `*x*` and `_x_`. The text inside a marker must not start or end
  // with a space, and `_x_` must stand alone (not inside a word like snake_case_name).
  const pattern = /\*\*(?=\S)([\s\S]*?\S)\*\*|__(?=\S)([\s\S]*?\S)__|\*(?=[^\s*])([^*\n]*?[^\s*])\*|(?<![\p{L}\p{N}])_(?=[^\s_])([^_\n]*?[^\s_])_(?![\p{L}\p{N}])/gu;
  let at = 0;
  for (const match of text.matchAll(pattern)) {
    push({ text: text.slice(at, match.index) });
    const bold = match[1] ?? match[2];
    if (bold !== undefined) {
      for (const inner of parseAssistantInline(bold)) push({ ...inner, bold: true });
    } else {
      push({ text: (match[3] ?? match[4])!, italic: true });
    }
    at = match.index + match[0].length;
  }
  push({ text: text.slice(at) });
  return spans;
}

/** The answer as one line of plain text, with no Markdown marks: for a preview in a list of chats. */
export function assistantPlainText(text: string): string {
  return parseAssistantText(text)
    .flatMap((block) => (block.type === "paragraph" ? [block.spans] : block.type === "list" ? block.items : block.rows.flat()))
    .map((spans) => spans.map((span) => span.text).join(""))
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
}
